import { useState, useEffect, useCallback, useRef } from "react";
import { Pencil, Trash2, Plus, Send, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DocTypeMultiSelect from "@/components/documents/DocTypeMultiSelect";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

const FREQUENCIES = ["instant", "daily", "weekly", "biweekly", "monthly"];

const LEDGER_EVENT_OPTIONS = [
  { value: "capital_call", label: "Capital calls" },
  { value: "distribution", label: "Distributions" },
];

const FREQUENCY_LABEL = {
  instant: "On arrival",
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
};

// Doc types + portals are sourced ENTIRELY from /api/documents/stats so the
// rule editor only ever shows what this tenant has actually scraped — same
// source the Documents page filter uses. Hardcoded fallbacks were removed
// after they polluted the dropdown with types the tenant has never received.

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
}

// ---------------------------------------------------------------------------
// MultiSelect — checkbox list inside a popover, with chips for selected items
// ---------------------------------------------------------------------------

function MultiSelect({ options, selected, onChange, placeholder = "— select —", emptyHint = "" }) {
  const [open, setOpen] = useState(false);

  // Look up the display label for a selected value. Options are either
  // strings (label === value) or {value, label, sub} objects. Falls back
  // to the raw value if no matching option is present (e.g. contacts that
  // were deleted after the rule was saved still need to show *something*).
  function labelFor(value) {
    for (const opt of options) {
      if (typeof opt === "string") {
        if (opt === value) return opt;
      } else if (opt.value === value) {
        return opt.label;
      }
    }
    return value;
  }

  function toggle(value) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  function clear(value, e) {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full min-h-10 items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer text-left"
        >
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {selected.length === 0 ? (
              <span className="text-gray-400">{placeholder}</span>
            ) : (
              selected.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 rounded bg-gray-100 text-gray-700 text-xs px-2 py-0.5"
                  title={s !== labelFor(s) ? s : undefined}
                >
                  {labelFor(s)}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => clear(s, e)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clear(s, e); }}
                    className="hover:text-gray-900 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </span>
                </span>
              ))
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1 max-h-72 overflow-y-auto"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        {options.length === 0 && (
          <div className="text-xs text-gray-400 p-2">{emptyHint || "No options."}</div>
        )}
        {options.map((opt) => {
          const value = typeof opt === "string" ? opt : opt.value;
          const label = typeof opt === "string" ? opt : opt.label;
          const sub = typeof opt === "string" ? null : opt.sub;
          const checked = selected.includes(value);
          return (
            <label
              key={value}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(value)}
              />
              <div className="flex-1 min-w-0">
                <div className="truncate">{label}</div>
                {sub && <div className="text-xs text-gray-400 truncate">{sub}</div>}
              </div>
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// IF/THEN/WHEN section primitives — declared at module scope so they keep a
// stable identity across renders. Defining them inside RuleDialog caused every
// keystroke to remount the inputs and drop focus.
// ---------------------------------------------------------------------------

function ConditionRow({ label, operator = "is any of", children }) {
  return (
    <div className="grid grid-cols-[140px_90px_1fr] gap-2 items-start">
      <div className="pt-2 text-sm font-medium text-gray-700">{label}</div>
      <div className="pt-2 text-xs text-gray-500 italic">{operator}</div>
      <div>{children}</div>
    </div>
  );
}

function SectionHeader({ kind, hint }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <span
        className="inline-block px-2 py-0.5 rounded text-xs font-bold tracking-wide"
        style={{ backgroundColor: BRAND.bg, color: "#fff" }}
      >
        {kind}
      </span>
      {hint && <span className="text-xs text-gray-500">{hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rule editor dialog
// ---------------------------------------------------------------------------

function RuleDialog({
  open,
  onClose,
  rule,
  onSaved,
  lpGroups,
  addressBookGroups,
  contacts,
  allPortals,
  docTypesByPortal,
  docTypeGroups,
}) {
  // Portal options: comprehensive list from /api/documents/stats.per_portal
  // (every portal with any active doc). Doc types are narrowed to selected
  // portals when any are picked, otherwise union across all.
  const portalOptions = Array.isArray(allPortals) ? allPortals : [];
  const isEdit = !!rule;
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [frequency, setFrequency] = useState("weekly");

  // Filters
  const [filterGroupIds, setFilterGroupIds] = useState([]);
  const [filterDocTypes, setFilterDocTypes] = useState([]);
  const [filterPortals, setFilterPortals] = useState([]);
  const [source, setSource] = useState("documents");
  const [filterFunds, setFilterFunds] = useState([]);
  const [filterEventTypes, setFilterEventTypes] = useState([]);
  const [fundOptions, setFundOptions] = useState([]);
  const [fundsError, setFundsError] = useState(false);
  const fundsRequested = useRef(false);
  const isLedger = source === "ledger";

  // Recipients
  const [recipientContactIds, setRecipientContactIds] = useState([]);
  const [recipientGroupName, setRecipientGroupName] = useState("");
  const [recipientExtraEmails, setRecipientExtraEmails] = useState("");

  // Format
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [subjectTemplate, setSubjectTemplate] = useState("");
  const [bodyTemplate, setBodyTemplate] = useState("");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setName(rule.name || "");
      setEnabled(rule.enabled ?? true);
      setFrequency(rule.frequency || "weekly");
      setFilterGroupIds(rule.filters?.group_ids || []);
      setFilterDocTypes(rule.filters?.doc_types || []);
      setFilterPortals(rule.filters?.portals || []);
      setSource(rule.source === "ledger" ? "ledger" : "documents");
      setFilterFunds(rule.filters?.funds || []);
      setFilterEventTypes(rule.filters?.event_types || []);
      setRecipientContactIds(rule.recipients?.contact_ids || []);
      setRecipientGroupName(rule.recipients?.group_name || "");
      setRecipientExtraEmails((rule.recipients?.extra_emails || []).join(", "));
      setIncludeAttachments(rule.format?.include_attachments ?? true);
      setSubjectTemplate(rule.format?.subject_template || "");
      setBodyTemplate(rule.format?.body_template || "");
    } else {
      setName("");
      setEnabled(true);
      setFrequency("weekly");
      setFilterGroupIds([]);
      setFilterDocTypes([]);
      setFilterPortals([]);
      setSource("documents");
      setFilterFunds([]);
      setFilterEventTypes([]);
      setRecipientContactIds([]);
      setRecipientGroupName("");
      setRecipientExtraEmails("");
      setIncludeAttachments(true);
      setSubjectTemplate("");
      setBodyTemplate("");
    }
  }, [open, rule]);

  // Fund names for the ledger picker: fetched once, only when Ledger is chosen.
  useEffect(() => {
    if (!open || !isLedger || fundsRequested.current) return;
    fundsRequested.current = true;
    api.get("/ledger/lines")
      .then((res) => {
        const lines = Array.isArray(res.data?.lines) ? res.data.lines : [];
        setFundOptions(Array.from(new Set(lines.map((l) => l.fund).filter(Boolean))).sort());
      })
      .catch(() => setFundsError(true));
  }, [open, isLedger]);

  async function handleSave() {
    if (!name.trim()) {
      toast.error("Rule name is required");
      return;
    }
    if (!isLedger && !FREQUENCIES.includes(frequency)) {
      toast.error("Invalid frequency");
      return;
    }
    // Guard: a rule with NO filters at all matches every doc the tenant
    // has. With include_attachments=on this can mass-attach hundreds of
    // PDFs in a single send. Force the user to confirm the broad scope.
    const hasAnyFilter =
      filterPortals.length > 0 ||
      filterDocTypes.length > 0 ||
      filterGroupIds.length > 0;
    if (!isLedger && !hasAnyFilter) {
      const proceed = await confirm({
        title: "Save a rule with no filters?",
        description:
          "This rule has no filters set, so it will match EVERY document the tenant receives. " +
          (includeAttachments
            ? "Attachments are ON, so every matching PDF will be included up to a 7 MB budget."
            : ""),
        confirmText: "Save anyway",
      });
      if (!proceed) return;
    }
    setSaving(true);
    const payload = {
      name: name.trim(),
      enabled,
      source,
      frequency: isLedger ? "instant" : frequency,
      filters: {
        group_ids: filterGroupIds,
        doc_types: isLedger ? [] : filterDocTypes,
        portals: filterPortals,
        funds: isLedger ? filterFunds : [],
        event_types: isLedger ? filterEventTypes : [],
      },
      recipients: {
        contact_ids: recipientContactIds,
        group_name: recipientGroupName || "",
        extra_emails: isLedger
          ? []
          : recipientExtraEmails
              .split(/[,\n]/)
              .map((s) => s.trim())
              .filter(Boolean),
      },
      format: {
        include_attachments: includeAttachments,
        subject_template: subjectTemplate,
        body_template: bodyTemplate,
      },
    };
    try {
      if (isEdit) {
        await api.patch(`/digest-rules/${rule.rule_id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/digest-rules", payload);
        toast.success("Rule created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  }

  const contactOptions = contacts.map((c) => ({
    value: c.id,
    label: c.name || c.email,
    sub: c.name ? c.email : null,
  }));
  const lpGroupOptions = lpGroups.map((g) => ({
    value: g.group_id,
    label: g.name,
    sub: `${(g.members || []).length} aliases`,
  }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit rule" : "New digest rule"}</DialogTitle>
          <DialogDescription>
            Build a rule like a sentence: <strong>If matches</strong> these conditions,{" "}
            <strong>Send to</strong> these recipients,{" "}
            <strong>Cadence</strong> on this schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Top: name + enabled */}
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>Rule name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Daniel's family — weekly tax + distributions"
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch
                id="rule-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
                style={{ backgroundColor: enabled ? BRAND.bg : undefined }}
              />
              <Label htmlFor="rule-enabled" className="cursor-pointer text-sm">
                {enabled ? "Enabled" : "Disabled"}
              </Label>
            </div>
          </div>

          {/* IF block */}
          <fieldset className="border border-gray-200 rounded-md p-4 space-y-3">
            <SectionHeader
              kind="If matches"
              hint="all conditions match (empty value = match anything)"
            />

            <ConditionRow label="Source" operator="is">
              <div className="flex items-center gap-4 pt-2 text-sm">
                {[["documents", "Documents"], ["ledger", "Ledger events"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="rule-source"
                      checked={source === v}
                      disabled={isEdit}
                      onChange={() => setSource(v)}
                    />
                    {l}
                  </label>
                ))}
              </div>
            </ConditionRow>

            {isLedger && (
              <>
                <ConditionRow label="Event types">
                  <MultiSelect
                    options={LEDGER_EVENT_OPTIONS}
                    selected={filterEventTypes}
                    onChange={setFilterEventTypes}
                    placeholder="any event type"
                  />
                </ConditionRow>
                <ConditionRow label="Funds">
                  <MultiSelect
                    options={fundOptions}
                    selected={filterFunds}
                    onChange={setFilterFunds}
                    placeholder="any fund"
                    emptyHint="No funds found."
                  />
                  {fundsError && (
                    <p className="text-xs text-gray-500 mt-1">
                      Could not load funds (ledger may not be enabled).
                    </p>
                  )}
                </ConditionRow>
              </>
            )}

            <ConditionRow label="Portal">
              <MultiSelect
                options={portalOptions}
                selected={filterPortals}
                onChange={setFilterPortals}
                placeholder="any portal"
                emptyHint="No portals known yet."
              />
            </ConditionRow>

            {!isLedger && (
            <ConditionRow label="Doc type">
              <DocTypeMultiSelect
                docTypeOptions={(() => {
                  const sourcePortals = filterPortals.length > 0
                    ? filterPortals
                    : Object.keys(docTypesByPortal || {});
                  const set = new Set();
                  sourcePortals.forEach((p) => {
                    (docTypesByPortal?.[p] || []).forEach((t) => t && set.add(t));
                  });
                  return Array.from(set).sort().map((t) => ({ id: t, name: t }));
                })()}
                docTypeGroups={docTypeGroups}
                selected={filterDocTypes}
                onChange={setFilterDocTypes}
                placeholderLabel="any doc type"
                triggerClassName="flex w-full min-h-10 items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer text-left"
              />
              {filterDocTypes.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2" data-testid="rule-doc-type-chips">
                  {filterDocTypes.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 rounded bg-gray-100 text-gray-700 text-xs px-2 py-0.5"
                    >
                      {t}
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${t}`}
                        onClick={() => setFilterDocTypes(filterDocTypes.filter((v) => v !== t))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            setFilterDocTypes(filterDocTypes.filter((v) => v !== t));
                        }}
                        className="hover:text-gray-900 cursor-pointer"
                      >
                        <X className="w-3 h-3" />
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </ConditionRow>
            )}

            <ConditionRow label="LP member is in">
              <MultiSelect
                options={lpGroupOptions}
                selected={filterGroupIds}
                onChange={setFilterGroupIds}
                placeholder="anyone"
                emptyHint="No LP groups yet — create one from the Documents page filter."
              />
            </ConditionRow>
          </fieldset>

          {/* THEN block */}
          <fieldset className="border border-gray-200 rounded-md p-4 space-y-3">
            <SectionHeader
              kind="Send to"
              hint="send via your connected Outlook (recipients are deduped)"
            />

            <ConditionRow label="To" operator="address book">
              <MultiSelect
                options={contactOptions}
                selected={recipientContactIds}
                onChange={setRecipientContactIds}
                placeholder="no specific contacts"
                emptyHint="No contacts yet — add them in the Email tab."
              />
            </ConditionRow>

            <ConditionRow label="To group" operator="address book">
              <Select
                value={recipientGroupName || "__none__"}
                onValueChange={(v) => setRecipientGroupName(v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— none —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {addressBookGroups.length === 0 && (
                    <div className="text-xs text-gray-400 px-2 py-1.5">
                      No groups yet. Add a contact with a group in the Email tab.
                    </div>
                  )}
                  {addressBookGroups.map((g) => (
                    <SelectItem key={g.group_name} value={g.group_name}>
                      {g.group_name} ({g.member_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ConditionRow>

            {!isLedger && (
              <ConditionRow label="CC" operator="direct emails">
                <Input
                  value={recipientExtraEmails}
                  onChange={(e) => setRecipientExtraEmails(e.target.value)}
                  placeholder="someone@example.com, another@example.com"
                />
              </ConditionRow>
            )}
          </fieldset>

          {/* WHEN block */}
          <fieldset className="border border-gray-200 rounded-md p-4 space-y-3">
            <SectionHeader kind="Cadence" hint="how often the rule fires" />

            <ConditionRow label="Cadence" operator="is">
              {isLedger ? (
                <div className="pt-2 text-sm">{FREQUENCY_LABEL.instant}</div>
              ) : (
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f} value={f}>
                      {FREQUENCY_LABEL[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </ConditionRow>

            <ConditionRow label="Attachments" operator="">
              <div className="flex items-start gap-2 pt-2">
                <Switch
                  id="rule-attach"
                  checked={includeAttachments}
                  onCheckedChange={setIncludeAttachments}
                  style={{ backgroundColor: includeAttachments ? BRAND.bg : undefined }}
                />
                <div>
                  <Label htmlFor="rule-attach" className="cursor-pointer text-sm">
                    {includeAttachments ? "Attach matching docs to the email" : "Subject + body only (no attachments)"}
                  </Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    On-arrival rules: the single doc is attached. Cadenced rules:
                    every matching doc since the last send is attached, up to a
                    7&nbsp;MB total budget — overflow is listed in the body.
                  </p>
                </div>
              </div>
            </ConditionRow>

            <ConditionRow label="Subject" operator="">
              <Input
                value={subjectTemplate}
                onChange={(e) => setSubjectTemplate(e.target.value)}
                placeholder="New {doc_type} from {portal}"
              />
            </ConditionRow>

            <ConditionRow label="Body" operator="">
              <textarea
                value={bodyTemplate}
                onChange={(e) => setBodyTemplate(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                placeholder="A new {doc_type} for {member_name} ({fund_name}) was received."
              />
            </ConditionRow>

            <p className="text-xs text-gray-500 pl-[230px]">
              Placeholders: {"{N}"}, {"{member_name}"}, {"{fund_name}"}, {"{doc_type}"}, {"{report_date}"}, {"{portal}"}, {"{date_range}"}
            </p>
          </fieldset>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: BRAND.bg, color: "#fff" }}
          >
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export default function DigestRulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lpGroups, setLpGroups] = useState([]);
  const [addressBookGroups, setAddressBookGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  // From /api/documents/stats. We separate the portals list from the
  // doc-types-by-portal map because they cover slightly different
  // populations: per_portal lists EVERY portal that has any active doc,
  // while doc_types_by_portal only lists portals that have docs with
  // scraper_doc_type_normalized populated. Sourcing portals from
  // doc_types_by_portal alone hides portals where the scraper raw-capture
  // rollout is still in progress (apex, intralinks, etc.).
  const [allPortals, setAllPortals] = useState([]);
  const [docTypesByPortal, setDocTypesByPortal] = useState({});
  // Server-canonical doc-type families from /documents/stats — drives the
  // grouped "select all Statements" picker (same source as the Documents page).
  const [docTypeGroups, setDocTypeGroups] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(null);

  // Per-rule "test-send in flight" set so the row's send button shows a
  // spinner the moment the user clicks instead of waiting for the Graph
  // round-trip (1-3s) before any visual feedback.
  const [testSendingIds, setTestSendingIds] = useState(() => new Set());

  const confirm = useConfirm();

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/digest-rules");
      setRules(Array.isArray(res.data?.rules) ? res.data.rules : []);
    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to load rules");
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [groupsRes, abGroupsRes, contactsRes, statsRes] = await Promise.allSettled([
        api.get("/groups"),
        api.get("/email/groups"),
        api.get("/email/contacts"),
        api.get("/documents/stats"),
      ]);

      if (groupsRes.status === "fulfilled") {
        setLpGroups(Array.isArray(groupsRes.value.data?.groups) ? groupsRes.value.data.groups : []);
      }

      if (abGroupsRes.status === "fulfilled") {
        setAddressBookGroups(Array.isArray(abGroupsRes.value.data) ? abGroupsRes.value.data : []);
      }

      if (contactsRes.status === "fulfilled") {
        setContacts(Array.isArray(contactsRes.value.data) ? contactsRes.value.data : []);
      }

      // Single source of truth for portals + doc types: /api/documents/stats.
      // Same path the Documents page filter uses — keeps the editor in sync
      // with what the tenant has actually scraped.
      if (statsRes.status === "fulfilled") {
        const data = statsRes.value.data || {};
        setDocTypesByPortal(data.doc_types_by_portal || {});
        setDocTypeGroups(Array.isArray(data.doc_type_groups) ? data.doc_type_groups : []);
        // Build the canonical portal list from per_portal (every portal that
        // has any active doc) with a fallback union over doc_types_by_portal
        // so we don't silently drop portals if the API shape changes.
        const fromPerPortal = Array.isArray(data.per_portal)
          ? data.per_portal.map((p) => p.portal).filter(Boolean)
          : [];
        const fromDtMap = Object.keys(data.doc_types_by_portal || {});
        const union = Array.from(new Set([...fromPerPortal, ...fromDtMap])).sort();
        setAllPortals(union);
      }
    } catch {
      // best-effort — partial data is fine
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadLookups();
  }, [loadRules, loadLookups]);

  function handleNew() {
    setEditingRule(null);
    setDialogOpen(true);
  }

  function handleEdit(rule) {
    setEditingRule(rule);
    setDialogOpen(true);
  }

  async function handleToggleEnabled(rule, next) {
    // Disabling stops the rule from firing — confirm. Enabling is harmless.
    if (!next) {
      const ok = await confirm({
        title: `Disable "${rule.name}"?`,
        description: "This rule will stop sending until you re-enable it.",
        confirmText: "Disable",
      });
      if (!ok) return;
    }
    try {
      await api.patch(`/digest-rules/${rule.rule_id}`, { enabled: next });
      toast.success(next ? "Rule enabled" : "Rule disabled");
      loadRules();
    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to toggle rule");
    }
  }

  async function handleTestSend(rule) {
    const rid = rule.rule_id;
    setTestSendingIds((prev) => new Set(prev).add(rid));
    const toastId = toast.loading(`Sending test for "${rule.name}"…`);
    try {
      const res = await api.post(`/digest-rules/${rid}/test-send`, {});
      const status = res.data?.status;
      const matched = res.data?.matched ?? 0;
      if (status === "skipped") {
        toast.warning(
          `Test send skipped: ${res.data?.reason || "no recipients"}`,
          { id: toastId },
        );
      } else if (status === "failed") {
        toast.error(
          `Test send failed: ${res.data?.error || "unknown"}`,
          { id: toastId },
        );
      } else {
        toast.success(
          `Test send queued (${matched} doc${matched === 1 ? "" : "s"} matched)`,
          { id: toastId },
        );
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || "Test send failed", { id: toastId });
    } finally {
      setTestSendingIds((prev) => {
        const next = new Set(prev);
        next.delete(rid);
        return next;
      });
    }
  }

  async function handleDelete() {
    if (!deletingRule) return;
    try {
      await api.delete(`/digest-rules/${deletingRule.rule_id}`);
      toast.success("Rule deleted");
      setDeletingRule(null);
      loadRules();
    } catch (err) {
      toast.error(getErrorMessage(err) || "Delete failed");
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Digest rules</CardTitle>
              <CardDescription>
                Send tenant-branded emails from your connected Outlook when matching
                docs arrive (<em>on arrival</em>) or on a cadence. Filter by LP group,
                doc type, and portal.
              </CardDescription>
            </div>
            <Button onClick={handleNew} style={{ backgroundColor: BRAND.bg, color: "#fff" }}>
              <Plus className="w-4 h-4 mr-1" /> New rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-gray-500">Loading rules…</div>
          ) : rules.length === 0 ? (
            <div className="text-sm text-gray-500">
              No rules yet. Click <strong>New rule</strong> to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead>Last sent</TableHead>
                  <TableHead>Fires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.rule_id}>
                    <TableCell className="font-medium">
                      {r.name}
                      {r.source === "ledger" && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-700">
                          Ledger rule
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-700">
                        {FREQUENCY_LABEL[r.frequency] || r.frequency}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!r.enabled}
                        onCheckedChange={(next) => handleToggleEnabled(r, next)}
                        style={{ backgroundColor: r.enabled ? BRAND.bg : undefined }}
                        aria-label={r.enabled ? "Disable rule" : "Enable rule"}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(r.last_sent_at)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {r.fire_count ?? 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleTestSend(r)}
                          disabled={testSendingIds.has(r.rule_id)}
                          title="Test send (last 7 days)"
                        >
                          {testSendingIds.has(r.rule_id) ? (
                            <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleEdit(r)}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setDeletingRule(r)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RuleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        rule={editingRule}
        onSaved={loadRules}
        lpGroups={lpGroups}
        addressBookGroups={addressBookGroups}
        contacts={contacts}
        allPortals={allPortals}
        docTypesByPortal={docTypesByPortal}
        docTypeGroups={docTypeGroups}
      />

      <AlertDialog open={!!deletingRule} onOpenChange={(o) => !o && setDeletingRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingRule
                ? `Permanently delete "${deletingRule.name}"? This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
